import { supabase } from '@/integrations/supabase/client';

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

async function authFetch(pathV1: string): Promise<any | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  const res = await fetch(`${BASE()}${pathV1}`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) return null;
  const json = await res.json();
  if (json && typeof json === 'object' && 'data' in json) return (json as any).data;
  return json;
}

export interface MeuSip {
  endpoint: string;
  secret: string;
  wss: string;
  domain: string;
}

/**
 * Busca as credenciais SIP/WebRTC do usuário logado (softphone Prime).
 * Backend: GET /api/v1/discadora/meu-sip. Retorna null se sem ramal válido.
 */
export async function getMeuSip(): Promise<MeuSip | null> {
  const data = await authFetch('/v1/discadora/meu-sip');
  return (data && typeof data === 'object') ? (data as MeuSip) : null;
}

export interface RamalInterno {
  equipe: string;
  nome: string;
  ramal: string;
  role: string;
  foto?: string | null;
}

/** Agenda interna de ramais (ativos com ramal válido), agrupável por equipe. */
export async function getRamaisInternos(): Promise<RamalInterno[]> {
  const data = await authFetch('/v1/discadora/ramais-internos');
  return Array.isArray(data) ? (data as RamalInterno[]) : [];
}

/** Grava no log do servidor uma falha do softphone. Nunca lança: é só rastro. */
export async function registrarFalhaSoftphone(info: {
  ramal: string | null; acao: string; motivo: string; janela: string; dona: boolean;
}): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    await fetch(`${BASE()}/v1/discadora/softphone-falha`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(info),
    });
  } catch { /* rastro não pode derrubar o telefone */ }
}

/** Ramais online agora (registrados no Asterisk) — presença na agenda. */
export async function getPresenca(): Promise<string[]> {
  const data = await authFetch('/v1/discadora/presenca');
  return Array.isArray(data) ? (data as string[]) : [];
}

export interface Gravacao {
  id: string;
  uniqueid: string;
  tipo: string;
  ramal_origem: string | null;
  destino: string | null;
  usuario_id: string | null;
  usuario_nome: string | null;
  direcao: string | null;
  inicio: string | null;
  duracao_segundos: number | null;
  disposition: string | null;
  virou_venda: boolean;
  created_at: string;
}

/** Histórico de chamadas gravadas. */
export async function getGravacoes(params?: { tipo?: string; q?: string; dias?: number; limit?: number }): Promise<Gravacao[]> {
  const qs = new URLSearchParams();
  if (params?.tipo) qs.set('tipo', params.tipo);
  if (params?.q) qs.set('q', params.q);
  if (params?.dias) qs.set('dias', String(params.dias));
  if (params?.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  const data = await authFetch(`/v1/discadora/gravacoes${s ? `?${s}` : ''}`);
  return Array.isArray(data) ? (data as Gravacao[]) : [];
}

/** URL assinada (30min) pra tocar/baixar a gravação. */
export async function getGravacaoUrl(id: string): Promise<{ url: string }> {
  const data = await authFetch(`/v1/discadora/gravacoes/${id}/url`);
  return (data && typeof data === 'object' && 'url' in data) ? (data as { url: string }) : { url: '' };
}

export interface LigacaoNaoTabulada {
  id: string;
  call_id: string;
  created_at: string;
  usuario_id: string;
  usuario_nome: string;
  consultor_nome: string;
  equipe_id: string | null;
  equipe_nome: string | null;
  duracao_segundos: number;
  lead_data: Record<string, unknown>;
}

export interface LigacoesNaoTabuladasPagina {
  items: LigacaoNaoTabulada[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export async function getLigacoesNaoTabuladas(params: {
  periodo: string;
  busca?: string;
  equipeId?: string;
  consultorId?: string;
  page: number;
}): Promise<LigacoesNaoTabuladasPagina> {
  const qs = new URLSearchParams({ periodo: params.periodo, page: String(params.page) });
  if (params.busca) qs.set('busca', params.busca);
  if (params.equipeId) qs.set('equipe_id', params.equipeId);
  if (params.consultorId) qs.set('consultor_id', params.consultorId);
  const data = await authFetch(`/v1/discadora/ligacoes-nao-tabuladas?${qs.toString()}`);
  return data && typeof data === 'object'
    ? data as LigacoesNaoTabuladasPagina
    : { items: [], totalCount: 0, page: params.page, pageSize: 100, totalPages: 0 };
}

export type FilaModoEstado = 'motor' | 'motor_indisponivel' | 'sem_campanha' | 'ramal_nao_liberado' | 'linx';
export interface FilaModo { id: string; nome: string; campanha_id?: string | null; campanha_status?: 'rascunho' | 'rodando' | 'pausada' | 'concluida' | null; tem_campanha: boolean; itens_pendentes: number; provedor?: 'linx' | 'ultracom'; classificador?: 'nenhum' | 'rota'; trunk?: string | null; prefixo?: string | null; modo: FilaModoEstado; motivo?: string | null; canais_configurados?: number | null; canais_teto?: number | null; canais_efetivos?: number | null; entregues_5m?: number; abandonos_5m?: number; abandono_pct_5m?: number; espera_livre_s?: number; espera_alvo_s?: number | null; ritmo_motivo?: string | null; }
export interface FilasModoResp { motor_ativo: boolean; motor_configurado?: boolean; motor_estado?: string; heartbeat_em?: string | null; classificador?: string | null; trunk?: string | null; prefixo?: string | null; ramais_piloto: string[]; ramal_atual?: string | null; ramal_elegivel?: boolean; operadora?: string; rota_ura?: { prefixo: string | null; estado: 'online' | 'offline' | 'desconhecido' | 'nao_instalado' | null }; filas: FilaModo[]; }

/** Por fila: 'motor' (nosso mailing/campanha) ou 'linx'. Base do caminho por fila. */
export async function getFilasModo(): Promise<FilasModoResp> {
  const d: any = (await authFetch('/v1/discadora/filas-modo')) || {};
  return {
    motor_ativo: !!d.motor_ativo,
    motor_configurado: !!d.motor_configurado,
    motor_estado: typeof d.motor_estado === 'string' ? d.motor_estado : undefined,
    heartbeat_em: typeof d.heartbeat_em === 'string' ? d.heartbeat_em : null,
    classificador: typeof d.classificador === 'string' ? d.classificador : null,
    trunk: typeof d.trunk === 'string' ? d.trunk : null,
    prefixo: typeof d.prefixo === 'string' ? d.prefixo : null,
    ramais_piloto: Array.isArray(d.ramais_piloto) ? d.ramais_piloto : [],
    ramal_atual: typeof d.ramal_atual === 'string' ? d.ramal_atual : null,
    ramal_elegivel: !!d.ramal_elegivel,
    operadora: typeof d.operadora === 'string' ? d.operadora : undefined,
    rota_ura: d.rota_ura && typeof d.rota_ura === 'object'
      ? { prefixo: typeof d.rota_ura.prefixo === 'string' ? d.rota_ura.prefixo : null, estado: typeof d.rota_ura.estado === 'string' ? d.rota_ura.estado : null }
      : undefined,
    filas: Array.isArray(d.filas) ? d.filas : [],
  };
}

/**
 * Resultado de um comando mandado pro motor da discadora.
 *
 * Nao basta `ok`: falhar porque a REDE caiu e falhar porque o MOTOR esta parado
 * pedem conselhos opostos. Na rede, "tente de novo" resolve; com o motor fora do
 * ar, tentar de novo vai falhar pra sempre e o certo e' avisar o suporte. Sem
 * essa distincao a tela dava o conselho errado justamente na hora ruim.
 * O backend marca o caso do motor com 503 (04/09/2026).
 */
export interface ResultadoMotor {
  /** o comando chegou ao servidor e foi aceito */
  ok: boolean;
  /** o servidor respondeu que o motor da discadora esta parado (503) */
  motorForaDoAr: boolean;
}

/**
 * Manda um comando pro motor e diz o que aconteceu.
 *
 * ⛔ Antes as tres funcoes abaixo eram `Promise<void>` com `catch {}` vazio: erro
 * 500, sessao expirada e rede caida ficavam IDENTICOS a sucesso, e a tela nao
 * tinha como avisar o consultor de nada. O `desligar` foi corrigido em 03/09; os
 * outros dois so' em 04/09 — e' o mesmo defeito, tres vezes.
 */
async function comandoMotor(rota: string, corpo: unknown): Promise<ResultadoMotor> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return { ok: false, motorForaDoAr: false };
    const r = await fetch(`${BASE()}/v1/discadora/${rota}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(corpo),
    });
    return { ok: r.ok, motorForaDoAr: r.status === 503 };
  } catch { return { ok: false, motorForaDoAr: false }; }
}

/** Motor: derruba SO o cliente da ligacao atual (mantem o consultor na sala; motor bridge o proximo). */
export function desligarClienteMotor(callId?: string | null): Promise<ResultadoMotor> {
  return comandoMotor('desligar-cliente', { call_id: callId ?? null });
}

/** Motor: liga/desliga a musiquinha (MOH) pro cliente enquanto o consultor esta em mudo. */
export function esperaClienteMotor(on: boolean): Promise<ResultadoMotor> {
  return comandoMotor('espera-cliente', { on });
}

/** Motor: transfere a perna do CLIENTE pro ramal destino (blind). */
export function transferirClienteMotor(ramalDestino: string, callId?: string | null): Promise<ResultadoMotor> {
  return comandoMotor('transferir-cliente', { ramal_destino: ramalDestino, call_id: callId ?? null });
}
